import React, { Component } from 'react';
import { AGGREGATE } from '../../util/enums/EnumLayers';
import AggregateOperation from '../../util/AggregateOperation';
import ColumnRenderer from '../../util/ColumnRenderer';
import ColumnsSelector from '../ColumnsSelector';
import DatetimeButtonGroup from '../DatetimeButtonGroup';
import { DATE, DATETIME } from '../../util/ColumnType';
import { dateOptions, DAY } from '../../util/enums/EnumDateTimeAggregation';
import DataTableBuilder from '../../util/DataTableBuilder';
import ModalLoading from './ModalLoading';

class ModalAggregate extends Component {
  static modalType() {
    return AGGREGATE;
  }

  static reducer(modals) {
    return modals.aggregateModal;
  }

  static title() {
    return 'Add summarize step';
  }

  aggButton(label, onClick, key) {
    return (
      <button
        key={key}
        type="button"
        className="btn btn-outline-dark margin-bottom-right calc-selected-option"
        onClick={onClick}
      >
        <span>{label}</span>
      </button>
    );
  }

  aggFunctions(columns, state, userInteraction, usedAliases, dbType) {
    const content = [];
    // existing aggregate functions
    state.aggFunctions.forEach((aF, index) => {
      const chosenFunction = this.aggButton(
        <span>
          <span className="close calc-selected-option-close">×</span>
          {aF.toString(state.pks, usedAliases)}
        </span>,
        userInteraction({ section: 'removeAgg', aggregateFunction: aF }),
        index,
      );
      content.push(chosenFunction);
    });

    const checkSupport = (ao, c) => {
      // don't support sum/avg of id columns (makes no sense)
      return !(['sum', 'avg'].includes(ao.name()) && c.name().name() === 'id');
    };

    // aggregate function builder
    const aggOps = AggregateOperation.all(dbType).filter((ao) =>
      ao
        .supportedTypes() // list of agg operations
        .some((t) =>
          columns // that can be applied to at least one column
            .some((c) => c.type().type() === t && checkSupport(ao, c)),
        ),
    );
    if (!state.moreClicked) {
      // nothing to show
    } else if (!state.selectedOp) {
      // if an aggregate op hasn't yet been clicked, show the options
      const funcOptions = [];
      state.pks
        .sort((a, b) => a.alias().localeCompare(b.alias()))
        .forEach((pk, index) => {
          const tableName = usedAliases[pk.alias()].name();
          const props = { section: 'addCount', columnName: pk };
          funcOptions.push(
            <button
              key={`count${index}`}
              type="button"
              className="btn btn-outline-dark"
              onClick={userInteraction(props)}
            >
              count {tableName}
            </button>,
          );
        });
      if (!state.countChecked) {
        funcOptions.push(
          <button
            key="count"
            type="button"
            className="btn btn-outline-dark"
            onClick={userInteraction({ section: 'countToggled' })}
          >
            row count
          </button>,
        );
      }
      aggOps.forEach((ao, index) => {
        const props = { section: 'chooseOp', aggregateOperation: ao };
        funcOptions.push(
          <button
            key={`agg${index}`}
            type="button"
            className="btn btn-outline-dark"
            onClick={userInteraction(props)}
          >
            {ao.longName()}
          </button>,
        );
      });
      const chooseFunction = (
        <table key="agg-options">
          <tbody>
            <tr key="choose">
              <td />
              <td colSpan={2}>
                <div className="btn-group-vertical modal_options">
                  {funcOptions}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      );
      content.push(chooseFunction);
    } else {
      // if an aggregate op has been chosen, show the columns that it can be applied to
      const renderer = new ColumnRenderer(
        'functionArg',
        userInteraction,
        usedAliases,
      );
      columns = columns.filter((c) =>
        state.selectedOp
          .supportedTypes()
          .some(
            (t) => t === c.type().type() && checkSupport(state.selectedOp, c),
          ),
      );
      const chooseColumn = (
        <table key="agg-cols">
          <tbody>
            <tr key="cols">
              <td className="padding-left-10">
                <input
                  type="checkbox"
                  checked
                  onChange={userInteraction({ section: 'undoMore' })}
                />
              </td>
              <td>
                <span>{state.selectedOp.name()} (</span>
              </td>
              <td>
                <ColumnsSelector
                  values={columns.map((c) => c.name())}
                  renderer={renderer}
                />
              </td>
              <td>)</td>
            </tr>
          </tbody>
        </table>
      );
      content.push(chooseColumn);
    }

    const countStar = !state.countChecked
      ? null
      : this.aggButton(
          <span>
            <span className="close calc-selected-option-close">×</span>row count{' '}
          </span>,
          userInteraction({ section: 'countToggled' }),
          'cnt',
        );
    const more = state.moreClicked
      ? null
      : this.aggButton(
          <span className="glyphicon glyphicon-plus" />,
          userInteraction({ section: 'moreClicked' }),
          'more',
        );

    return (
      <div>
        {countStar}
        {content}
        {more}
      </div>
    );
  }

  groupBy(columns, state, userInteraction, usedAliases) {
    const selectedColumns = columns.filter((c) =>
      state.selected.some((aggCol) => aggCol.columnName().equals(c.name())),
    );
    const chosenTables = selectedColumns.filter((c) =>
      state.pks.some((pk) => pk.equals(c.name())),
    );
    columns = columns.filter(
      (c) =>
        !state.selected.some((aggCol) => aggCol.columnName().equals(c.name())),
    ); // don't let user select same column twice
    columns = columns.filter((c) => c.type().operators()); // only allow aggregating on types that support comparison (e.g. json doesn't)
    columns = columns.filter(
      (c) => !chosenTables.some((pk) => pk.name().alias() === c.name().alias()),
    ); // don't allow choosing columns from table that has been chosen

    const tablesWithPks = state.pks
      .filter(
        (pk) => !selectedColumns.some((c) => c.name().alias() === pk.alias()),
      ) // can't choose a table if one of its columns has already been chosen
      .map((pk, i) => (
        <a
          key={i}
          href="#"
          onClick={userInteraction({ section: 'addColumn', columnName: pk })}
          className="list-group-item"
        >
          <b>{usedAliases[pk.alias()].name()}</b>
        </a>
      ));
    const tableChoices =
      tablesWithPks.length === 0 ? null : (
        <div className="panel panel-default">
          <div className="card-header text-align-center">suggested</div>
          <div className="list-group text-align-center hide-overflow">
            {tablesWithPks}
          </div>
        </div>
      );

    const extractAggregation = (columnName) => {
      const column = columns.find((c) => c.name().equals(columnName));
      return column.type().type() === DATE || column.type().type() === DATETIME
        ? DAY
        : null;
    };
    const renderer = new ColumnRenderer(
      'addColumn',
      userInteraction,
      usedAliases,
      extractAggregation,
    );
    let options = (
      <div>
        {tableChoices}
        <ColumnsSelector
          values={columns.map((c) => c.name())}
          renderer={renderer}
        />
      </div>
    );

    const selected = state.selected.map((aggCol) => {
      const props = { section: 'removeColumn', aggCol };
      const column = selectedColumns.find((c) =>
        c.name().equals(aggCol.columnName()),
      );
      if (!column) {
        return null;
      }

      const changeAgg = (aggregation) => ({
        section: 'changeAgg',
        aggCol,
        aggregation,
      });

      const aggName = aggCol.aggregation();

      const onClick = (aggType) => {
        const payload = changeAgg(aggType);
        userInteraction(payload)();
      };

      const choice =
        column.type().type() !== DATE &&
        column.type().type() !== DATETIME ? null : (
          <DatetimeButtonGroup
            handleClick={onClick}
            aggName={aggName}
            options={dateOptions}
          />
        );
      const asColumn = aggCol.columnName();
      const isPk = state.pks.some((pk) => pk.equals(asColumn));
      const name = isPk ? usedAliases[asColumn.alias()].name() : aggCol.name();
      return (
        <div
          key={aggCol.toString()}
          className="btn-group display-flex float-left margin-bottom-right"
        >
          <button
            type="button"
            className="btn btn-outline-dark calc-selected-option"
            onClick={userInteraction(props)}
          >
            <span className="close calc-selected-option-close">×</span> {name}
          </button>
          {choice}
        </div>
      );
    });
    let emptySpace = <div className="clear-both" />;
    if (!state.plusClicked) {
      const props = { section: 'plusClicked' };
      emptySpace = (
        <button
          type="button"
          className="btn btn-outline-dark float-left calc-selected-option"
          onClick={userInteraction(props)}
        >
          <span className="glyphicon glyphicon-plus" />
        </button>
      );
      if (!tableChoices && columns.length === 0) {
        emptySpace = null;
      }
      options = [];
    }
    return (
      <div>
        {selected}
        {emptySpace}
        {options}
      </div>
    );
  }

  preview(state, usedAliases, userInteraction) {
    const buildHeader = (columnName, index) => {
      const tovName =
        columnName.alias() && usedAliases[columnName.alias()]
          ? usedAliases[columnName.alias()].name()
          : null;
      const isExtraColumn = state.extraColumns.some((cn) =>
        cn.equals(columnName),
      );
      const onClick = () => {
        if (isExtraColumn) {
          userInteraction({ section: 'remove-col', columnName })();
        }
      };
      return (
        <td
          key={index}
          onClick={onClick}
          className={`dt-header-cell hide-overflow${
            isExtraColumn ? ' can-delete' : ''
          }`}
        >
          {!isExtraColumn ? null : (
            <span className="close calc-selected-option-close">×</span>
          )}
          <span className="table-header-container">
            <span className="hidden-data-table-header">{tovName}</span>
          </span>
          <br />
          <b className="data-table-header">{columnName.name()}</b>
        </td>
      );
    };
    let headers = null;
    let data = (
      <tr key="loader">
        <td className="no-border" />
        <td className="no-border">
          <ModalLoading />
        </td>
      </tr>
    );
    if (state.error) {
      data = (
        <tr>
          <td className="no-border text-align-center max-width-none">
            <div className="alert alert-danger" role="alert">
              {state.error}
            </div>
          </td>
        </tr>
      );
    } else if (state.results) {
      headers = state.results.headings().map((heading, hIndex) => {
        const columnName = heading.columnName();
        return buildHeader(columnName, hIndex);
      });
      data = new DataTableBuilder().build(state.results);
    } else if (state.previewColumns) {
      headers = state.previewColumns.map((columnName, index) => {
        return buildHeader(columnName, index);
      });
    }
    if (headers && headers.length === 0) {
      data = (
        <tr>
          <td className="no-border text-align-center max-width-none">
            <div className="choose-calculation">
              <h5>
                Get started by choosing a{' '}
                <span className="calc-underline">metric</span>.
              </h5>
            </div>
          </td>
        </tr>
      );
    }
    return { headers, data };
  }

  additionalColumns(state, columns, userInteraction) {
    const chosenPks = state.selected.filter((ac) =>
      state.pks.some((pk) => pk.equals(ac.columnName())),
    ); // these are the selected primary keys
    const additionalColumns = columns
      .filter((c) =>
        chosenPks.some(
          (pk) => pk.alias() === c.name().alias() && !c.name().equals(pk),
        ),
      ) // these columns are the from the table of the selected primary keys
      .filter((c) => !state.extraColumns.some((cn) => c.name().equals(cn))) // don't include columns more than once
      .filter(
        (c) =>
          !state.selected.some((aggCol) =>
            aggCol.columnName().equals(c.name()),
          ),
      ); // don't include if already part of GROUP BY
    if (additionalColumns.length === 0) {
      return null;
    }
    const additionalColButtons = additionalColumns.map((c) => {
      return (
        <button
          key={c.name().toString()}
          onClick={userInteraction({ section: 'include-col', column: c })}
          type="button"
          className="btn btn-outline-dark margin-bottom-right calc-selected-option"
        >
          {c.name().name()}
        </button>
      );
    });
    return (
      <div className="calc-additional-columns fixed-below-navbar">
        <h5 className="calc-h5-title">3. Include other columns</h5>
        {additionalColButtons}
      </div>
    );
  }

  render() {
    const { state, userInteraction, usedAliases, dbType } = this.props;

    const columns = state.availableColumns; // this includes pks that may be hidden in usual 'columns' variable
    const groupBy = this.groupBy(columns, state, userInteraction, usedAliases);
    const aggFunctions = this.aggFunctions(
      columns,
      state,
      userInteraction,
      usedAliases,
      dbType,
    );
    const additionalColumns = this.additionalColumns(
      state,
      columns,
      userInteraction,
    );
    const preview = this.preview(state, usedAliases, userInteraction);

    return (
      <div className="calc-modal">
        <div className="calc-top-left fixed-below-navbar">
          <h5 className="calc-h5-title">
            1. Metrics{' '}
            <span className="learn-more">
              <a
                href="https://blog.trevor.io/common-calculations-in-trevor"
                target="_blank"
              >
                Learn more
              </a>
            </span>
          </h5>
          {aggFunctions}
        </div>
        <div className="calc-bottom-left">
          <h5 className="calc-h5-title">2. Group results by</h5>
          {groupBy}
        </div>
        <div className="">
          {additionalColumns}
          <div
            className={`calc-main${
              additionalColumns ? ' below-additional-columns' : ''
            }`}
          >
            <h5 className="calc-h5-title margin-left-20">Live Preview</h5>
            <div className="margin-top-30">
              <table className="data-table">
                <tbody>
                  <tr className="thead">
                    <td className="no-border" />
                    {preview.headers}
                  </tr>
                  {preview.data}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ModalAggregate;
